import React, { useState, useMemo } from 'react';
import { Plus, ChevronRight, ChevronLeft, Trash2, Edit2, Building2 } from 'lucide-react';
import { Card, SectionHeader, Btn, Badge, Modal, Field, Input, EmptyState, Table, TD } from '../components/UI';
import { fmtAmt } from '../utils/helpers';

// ─── Helpers ─────────────────────────────────────────────────────
function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function nowMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
}
function monthsBetween(startYM, endYM) {
  // Returns array of 'YYYY-MM' strings from start up to and including end
  const [sy, sm] = startYM.split('-').map(Number);
  const [ey, em] = endYM.split('-').map(Number);
  const months = [];
  let y = sy, m = sm;
  while (y < ey || (y === ey && m <= em)) {
    months.push(`${y}-${String(m).padStart(2,'0')}`);
    m++; if (m > 12) { m = 1; y++; }
  }
  return months;
}
function nextInvoiceNum(invoices) {
  const nums = (invoices||[]).map(i => parseInt((i.invoiceNumber||'').replace(/\D/g,''))||0);
  return 'INV-' + String(Math.max(0,...nums)+1).padStart(6,'0');
}

const sel = { width:'100%',padding:'9px 12px',fontSize:13,background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:'var(--radius)',color:'var(--text)',boxSizing:'border-box' };
const inp = { width:'100%',padding:'9px 12px',fontSize:13,background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:'var(--radius)',color:'var(--text)',boxSizing:'border-box' };

// ─── Empty forms ─────────────────────────────────────────────────
const emptyParent = () => ({ name:'', contactName:'', contactEmail:'', notes:'' });
const emptySub    = () => ({ name:'', amount:'', dueDay:'1', startDate:'', adType:'', socialMedia:false, active:true, notes:'' });
const emptyProject= () => ({ name:'', amount:'', date:today(), notes:'' });

export default function ParentClientsPage({ store, persist, showToast }) {
  const parentClients  = store.parentClients  || [];
  const allInvoices    = store.invoices       || [];
  const allClients     = store.clients        || [];

  const [view,       setView]       = useState('list');   // 'list' | 'detail'
  const [selectedId, setSelectedId] = useState(null);

  // Modals
  const [showParentModal,  setShowParentModal]  = useState(false);
  const [showSubModal,     setShowSubModal]     = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(null); // sub-client object
  const [editingParent,    setEditingParent]    = useState(null);
  const [editingSub,       setEditingSub]       = useState(null);

  const [parentForm,  setParentForm]  = useState(emptyParent());
  const [subForm,     setSubForm]     = useState(emptySub());
  const [projectForm, setProjectForm] = useState(emptyProject());
  const [historyForm,  setHistoryForm]  = useState({ startDate:'', endDate:nowMonth() });
  const [showPayModal,    setShowPayModal]    = useState(false);
  const [payAmount,       setPayAmount]       = useState('');
  const [showGenModal,    setShowGenModal]    = useState(false);
  const [genSub,          setGenSub]          = useState(null);
  const [genAmount,       setGenAmount]       = useState('');

  const selected = parentClients.find(p => p.id === selectedId);

  // ── Parent CRUD ──────────────────────────────────────────────────
  function saveParent() {
    if (!parentForm.name.trim()) return showToast('Company name required.','error');
    let updated;
    if (editingParent) {
      updated = { ...store, parentClients: parentClients.map(p => p.id===editingParent.id ? {...p,...parentForm} : p) };
      showToast('Agency updated.');
    } else {
      const entry = { id: Date.now().toString(), ...parentForm, subClients:[], projects:[] };
      updated = { ...store, parentClients: [...parentClients, entry] };
      showToast('Agency added.');
    }
    persist(updated); setShowParentModal(false);
  }

  function deleteParent(id) {
    if (!window.confirm('Delete this billing agency and all its sub-clients?')) return;
    persist({ ...store, parentClients: parentClients.filter(p => p.id !== id) }, { deletedIds: { parentClients: [id] } });
    setView('list'); showToast('Agency deleted.');
  }

  // ── Sub-client CRUD ──────────────────────────────────────────────
  function saveSub() {
    if (!subForm.name.trim() || !subForm.amount || !subForm.startDate) {
      return showToast('Name, monthly amount and start date are required.','error');
    }
    const sub = editingSub
      ? { ...editingSub, ...subForm, amount: Number(subForm.amount), dueDay: Number(subForm.dueDay) }
      : { id: Date.now().toString(), ...subForm, amount: Number(subForm.amount), dueDay: Number(subForm.dueDay) };

    const updated = { ...store, parentClients: parentClients.map(p => {
      if (p.id !== selectedId) return p;
      const subs = editingSub
        ? (p.subClients||[]).map(s => s.id===editingSub.id ? sub : s)
        : [...(p.subClients||[]), sub];
      return { ...p, subClients: subs };
    })};
    persist(updated);
    showToast(editingSub ? 'Sub-client updated.' : 'Sub-client added.');
    setShowSubModal(false);
  }

  function deleteSub(subId) {
    if (!window.confirm('Remove this sub-client?')) return;
    const updated = { ...store, parentClients: parentClients.map(p => {
      if (p.id !== selectedId) return p;
      return { ...p, subClients: (p.subClients||[]).filter(s => s.id !== subId) };
    })};
    persist(updated); showToast('Sub-client removed.');
  }

  // ── One-time project CRUD ────────────────────────────────────────
  function saveProject() {
    if (!projectForm.name.trim() || !projectForm.amount) {
      return showToast('Project name and amount required.','error');
    }
    const proj = { id: Date.now().toString(), ...projectForm, amount: Number(projectForm.amount), status:'unpaid' };
    const updated = { ...store, parentClients: parentClients.map(p => {
      if (p.id !== selectedId) return p;
      return { ...p, projects: [...(p.projects||[]), proj] };
    })};
    persist(updated); setShowProjectModal(false); showToast('Project added.');
  }

  function updateProjectStatus(projId, status) {
    const updated = { ...store, parentClients: parentClients.map(p => {
      if (p.id !== selectedId) return p;
      const projects = (p.projects||[]).map(pr => pr.id===projId ? {...pr, status} : pr);
      return { ...p, projects };
    })};
    persist(updated); showToast('Project status updated.');
  }

  function deleteProject(projId) {
    const updated = { ...store, parentClients: parentClients.map(p => {
      if (p.id !== selectedId) return p;
      return { ...p, projects: (p.projects||[]).filter(pr => pr.id !== projId) };
    })};
    persist(updated); showToast('Project deleted.');
  }

  // ── Historical invoice generator ─────────────────────────────────
  function generateHistory() {
    const sub = showHistoryModal;
    if (!sub || !historyForm.startDate) return showToast('Start date required.','error');
    const months = monthsBetween(historyForm.startDate, historyForm.endDate);
    if (months.length === 0) return showToast('No months in range.','error');

    const newInvoices = [];
    months.forEach(ym => {
      // Don't duplicate
      const exists = allInvoices.find(i => i.subClientId===sub.id && i.monthYear===ym);
      if (exists) return;
      newInvoices.push({
        id:            Date.now().toString() + Math.random().toString(36).slice(2),
        clientId:      selectedId,   // parent client is the billing entity
        subClientId:   sub.id,
        subClientName: sub.name,
        amount:        sub.amount,
        status:        'generated',
        monthYear:     ym,
        invoiceNumber: nextInvoiceNum([...allInvoices,...newInvoices]),
        generatedDate: today(),
        isHistorical:  true,
      });
    });

    if (newInvoices.length === 0) return showToast('All months already have invoices.','error');
    persist({ ...store, invoices: [...allInvoices, ...newInvoices] });
    showToast(`${newInvoices.length} historical invoice(s) generated.`);
    setShowHistoryModal(null);
  }

  // ── Record payment with rolling credit ─────────────────────────────
  function recordPayment(parentId, paymentAmount) {
    if (!paymentAmount || paymentAmount <= 0) return;
    const parent   = parentClients.find(p => p.id === parentId);
    if (!parent) return;

    // All sub-client IDs for this parent
    const subClientIds = (parent.subClients || []).map(s => s.id);

    // Get all invoices belonging to this parent (direct or sub-client)
    const parentInvoices = allInvoices
      .filter(i => i.clientId === parentId || subClientIds.includes(i.clientId))
      .slice()
      .sort((a, b) => {
        // Sort chronologically by monthYear, then by generatedDate
        const ym = (a.monthYear || '').localeCompare(b.monthYear || '');
        if (ym !== 0) return ym;
        return (a.generatedDate || '').localeCompare(b.generatedDate || '');
      });

    // Start with existing credit balance
    let credit = parent.creditBalance || 0;
    credit += paymentAmount;

    // Apply credit to unpaid invoices chronologically
    const updatedInvoices = allInvoices.map(inv => {
      // Only touch invoices for this parent
      if (inv.clientId !== parentId && !subClientIds.includes(inv.clientId)) return inv;
      if (inv.status === 'paid') return inv; // already paid — skip

      // Find this invoice in our sorted list
      const sorted = parentInvoices.filter(i => i.status !== 'paid');
      const isNext  = sorted[0] && sorted[0].id === inv.id;
      if (!isNext || credit <= 0) return inv;

      // Can we fully pay this invoice?
      if (credit >= inv.amount) {
        credit -= inv.amount;
        return { ...inv, status: 'paid', paidDate: new Date().toISOString().slice(0, 10) };
      }
      // Not enough to pay — stop here, leave pending
      return inv;
    });

    // This approach doesn't mutate parentInvoices as we go — fix: apply sequentially
    // Re-do with a mutable credit tracker
    let runningCredit = (parent.creditBalance || 0) + paymentAmount;
    const sortedUnpaid = parentInvoices
      .filter(i => i.status !== 'paid')
      .sort((a, b) => {
        const ym = (a.monthYear || '').localeCompare(b.monthYear || '');
        if (ym !== 0) return ym;
        return (a.generatedDate || '').localeCompare(b.generatedDate || '');
      });

    const nowPaid = new Set();
    for (const inv of sortedUnpaid) {
      if (runningCredit >= inv.amount) {
        runningCredit -= inv.amount;
        nowPaid.add(inv.id);
      } else {
        break; // not enough to pay this one — stop
      }
    }

    const finalInvoices = allInvoices.map(inv => {
      if (!nowPaid.has(inv.id)) return inv;
      return { ...inv, status: 'paid', paidDate: new Date().toISOString().slice(0, 10) };
    });

    // Update the parent client's credit balance
    const updatedParents = parentClients.map(p => {
      if (p.id !== parentId) return p;
      return { ...p, creditBalance: runningCredit };
    });

    const paidCount = nowPaid.size;
    persist({ ...store, invoices: finalInvoices, parentClients: updatedParents });
    showToast(
      `Payment of ${fmtAmt(paymentAmount)} applied. ${paidCount} invoice${paidCount !== 1 ? 's' : ''} marked paid.`
      + (runningCredit > 0 ? ` Credit balance: ${fmtAmt(runningCredit)}.` : '')
    );
  }

  // ── Generate invoice for a sub-client ──────────────────────────────
  function generateSubInvoice(sub, overrideAmount) {
    const amount    = overrideAmount || sub.amount;
    const monthYear = nowMonth();
    // Don't duplicate
    const exists = allInvoices.find(i =>
      i.clientId === sub.id && i.monthYear === monthYear
    );
    if (exists) {
      showToast(`Invoice already exists for ${sub.name} this month.`, 'error');
      return;
    }
    // Build next invoice number
    const maxNum = Math.max(0, ...allInvoices.map(i =>
      parseInt((i.invoiceNumber || '').replace(/\D/g, '')) || 0
    ));
    const inv = {
      id:            Date.now().toString(),
      clientId:      sub.id,
      parentClientId: selectedId,
      amount,
      status:        'generated',
      monthYear,
      invoiceNumber: 'INV-' + String(maxNum + 1).padStart(6, '0'),
      generatedDate: today(),
      notes:         `Generated for ${selected?.name || 'agency'}`,
    };
    persist({ ...store, invoices: [...allInvoices, inv] });
    showToast(`Invoice ${inv.invoiceNumber} generated for ${sub.name} — ${fmtAmt(amount)}.`);
  }

  // ── Outstanding summary ───────────────────────────────────────────
  function getOutstanding(parentId) {
    const parent = parentClients.find(p => p.id === parentId);
    if (!parent) return { total:0, count:0 };
    const subIds = (parent.subClients||[]).map(s => s.id);
    const relevant = allInvoices.filter(i => i.clientId===parentId || subIds.includes(i.subClientId));
    const unpaid   = relevant.filter(i => i.status !== 'paid');
    return { total: unpaid.reduce((s,i) => s+(i.amount||0),0), count: unpaid.length };
  }

  // ── List view ────────────────────────────────────────────────────
  if (view === 'list') return (
    <div style={{display:'flex',flexDirection:'column',gap:16}}>
      <Card>
        <SectionHeader
          title="Billing Agencies & Outsource Partners"
          count={parentClients.length}
          action={<Btn size="sm" onClick={()=>{setEditingParent(null);setParentForm(emptyParent());setShowParentModal(true);}}>
            <Plus size={12}/>Add agency
          </Btn>}
        />
        {parentClients.length===0
          ? <EmptyState message="No billing agencies yet. Add one to manage outsourced client billing."/>
          : parentClients.map(p => {
            const out = getOutstanding(p.id);
            return (
              <div key={p.id} onClick={()=>{setSelectedId(p.id);setView('detail');}}
                style={{padding:'14px 18px',borderBottom:'1px solid var(--border)',cursor:'pointer',display:'flex',alignItems:'center',gap:12,transition:'background 0.1s'}}
                onMouseEnter={e=>e.currentTarget.style.background='var(--bg3)'}
                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                <div style={{width:38,height:38,borderRadius:'var(--radius)',background:'var(--bg3)',border:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                  <Building2 size={16} color="var(--accent)"/>
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:600,fontSize:14,color:'var(--text)',marginBottom:2}}>{p.name}</div>
                  <div style={{fontSize:12,color:'var(--text3)'}}>
                    {(p.subClients||[]).length} sub-client{(p.subClients||[]).length!==1?'s':''} &nbsp;·&nbsp;
                    {(p.projects||[]).length} project{(p.projects||[]).length!==1?'s':''}
                    {p.contactName && <>&nbsp;·&nbsp;{p.contactName}</>}
                  </div>
                </div>
                {out.count > 0 && (
                  <div style={{textAlign:'right',flexShrink:0}}>
                    <div style={{fontSize:11,color:'var(--orange)',fontWeight:600}}>{out.count} outstanding</div>
                    <div style={{fontSize:13,fontWeight:700,color:'var(--text)',fontFamily:'var(--mono)',fontVariantNumeric:'lining-nums tabular-nums'}}>{fmtAmt(out.total)}</div>
                  </div>
                )}
                <ChevronRight size={14} color="var(--text3)"/>
              </div>
            );
          })
        }
      </Card>

      {/* Parent modal */}
      <Modal open={showParentModal} onClose={()=>setShowParentModal(false)} title={editingParent?'Edit agency':'Add billing agency'}>
        <Field label="Company / Agency name"><Input value={parentForm.name} onChange={e=>setParentForm(p=>({...p,name:e.target.value}))} placeholder="e.g. The Parthenon Consultancy" autoFocus/></Field>
        <Field label="Contact person (optional)"><Input value={parentForm.contactName} onChange={e=>setParentForm(p=>({...p,contactName:e.target.value}))} placeholder="e.g. Ali Khan"/></Field>
        <Field label="Contact email (optional)"><Input type="email" value={parentForm.contactEmail} onChange={e=>setParentForm(p=>({...p,contactEmail:e.target.value}))} placeholder="ali@company.com"/></Field>
        <Field label="Notes (optional)">
          <textarea value={parentForm.notes} onChange={e=>setParentForm(p=>({...p,notes:e.target.value}))} rows={2} style={{...inp,resize:'vertical',fontFamily:'var(--font)'}} placeholder="Any notes about this agency…"/>
        </Field>
        <div style={{display:'flex',justifyContent:'flex-end',gap:8,marginTop:'1rem'}}>
          <Btn onClick={()=>setShowParentModal(false)}>Cancel</Btn>
          <Btn variant="primary" onClick={saveParent}>{editingParent?'Save changes':'Add agency'}</Btn>
        </div>
      </Modal>
    </div>
  );

  // ── Detail view ───────────────────────────────────────────────────
  if (!selected) { setView('list'); return null; }
  const subInvoices = allInvoices.filter(i => i.clientId===selectedId || (selected.subClients||[]).some(s=>s.id===i.subClientId));

  return (
    <div style={{display:'flex',flexDirection:'column',gap:16}}>
      {/* Header */}
      <div style={{display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
        <Btn size="sm" variant="ghost" onClick={()=>setView('list')}><ChevronLeft size={14}/>All agencies</Btn>
        <div style={{flex:1}}/>
        <Btn size="sm" variant="primary" onClick={()=>{setPayAmount('');setShowPayModal(true);}}>
          Record payment
        </Btn>
        <Btn size="sm" variant="ghost" onClick={()=>{setEditingParent(selected);setParentForm({name:selected.name,contactName:selected.contactName||'',contactEmail:selected.contactEmail||'',notes:selected.notes||''});setShowParentModal(true);}}>
          <Edit2 size={11}/>Edit
        </Btn>
        <Btn size="sm" variant="danger" onClick={()=>deleteParent(selected.id)}><Trash2 size={11}/>Delete</Btn>
      </div>

      {/* Agency info card */}
      <Card>
        <div style={{padding:'1.25rem 1.5rem'}}>
          <div style={{display:'flex',alignItems:'flex-start',gap:14}}>
            <div style={{width:44,height:44,borderRadius:'var(--radius)',background:'var(--bg3)',border:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
              <Building2 size={20} color="var(--accent)"/>
            </div>
            <div>
              <div style={{fontWeight:400,fontSize:18,color:'var(--text)',marginBottom:4,fontFamily:'var(--serif)'}}>{selected.name}</div>
              {selected.contactName && <div style={{fontSize:13,color:'var(--text3)',marginBottom:2}}>Contact: {selected.contactName}{selected.contactEmail && ` · ${selected.contactEmail}`}</div>}
              {selected.notes && (() => {
                // Check if notes contain reconciliation data (pipe-separated)
                const parts = selected.notes.split('|').map(s=>s.trim());
                if (parts.length > 1) {
                  return (
                    <div style={{marginTop:10,display:'flex',gap:8,flexWrap:'wrap'}}>
                      {parts.map((part,i) => {
                        const [label, value] = part.split(':').map(s=>s.trim());
                        return (
                          <div key={i} style={{background:'var(--bg4)',borderRadius:'var(--radius)',padding:'6px 12px',fontSize:12}}>
                            <span style={{color:'var(--text3)'}}>{label}: </span>
                            <span style={{fontWeight:600,color:i===2?'var(--orange)':'var(--text)'}}>{value}</span>
                          </div>
                        );
                      })}
                    </div>
                  );
                }
                return <div style={{fontSize:12,color:'var(--text3)',marginTop:4,fontStyle:'italic'}}>{selected.notes}</div>;
              })()}
            </div>
          </div>
          {/* Financials summary */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginTop:16}}>
            {[
              ['Sub-clients', (selected.subClients||[]).length, 'var(--blue)'],
              ['Projects', (selected.projects||[]).length, 'var(--purple)'],
              ['Outstanding', fmtAmt(getOutstanding(selected.id).total), 'var(--orange)'],
              ...(selected.creditBalance > 0 ? [['Credit balance', fmtAmt(selected.creditBalance), 'var(--green)']] : []),
            ].map(([l,v,col])=>(
              <div key={l} style={{background:'var(--bg3)',borderRadius:'var(--radius)',padding:'12px 14px',textAlign:'center'}}>
                <div style={{fontSize:10,color:'var(--text3)',fontWeight:400,textTransform:'uppercase',letterSpacing:'0.1em',marginBottom:4,fontFamily:'var(--mono)'}}>{l}</div>
                <div style={{fontSize:18,fontWeight:700,color:col,fontFamily:'var(--mono)',fontVariantNumeric:'lining-nums tabular-nums'}}>{v}</div>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Sub-clients */}
      <Card>
        <SectionHeader title="Sub-clients" count={(selected.subClients||[]).length}
          action={<Btn size="sm" onClick={()=>{setEditingSub(null);setSubForm(emptySub());setShowSubModal(true);}}>
            <Plus size={12}/>Add sub-client
          </Btn>}
        />
        {(selected.subClients||[]).length===0
          ? <EmptyState message="No sub-clients yet. Add one to start generating invoices for this agency."/>
          : (selected.subClients||[]).map(sub=>{
            const subInvs = allInvoices.filter(i=>i.subClientId===sub.id);
            const unpaid  = subInvs.filter(i=>i.status!=='paid');
            return (
              <div key={sub.id} style={{padding:'14px 18px',borderBottom:'1px solid var(--border)'}}>
                <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:12,flexWrap:'wrap'}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4,flexWrap:'wrap'}}>
                      <span style={{fontWeight:600,fontSize:14,color:'var(--text)'}}>{sub.name}</span>
                      {sub.active === false && <Badge label="Paused" color="gray"/>}
                      {sub.adType && <Badge label={sub.adType} color="purple"/>}
                      {sub.socialMedia && <Badge label="Social Media" color="blue"/>}
                    </div>
                    <div style={{fontSize:12,color:'var(--text3)'}}>
                      {fmtAmt(sub.amount)}/mo &nbsp;·&nbsp; Due: {sub.dueDay}{['st','nd','rd'][sub.dueDay-1]||'th'} &nbsp;·&nbsp; Since: {sub.startDate||'—'}
                    </div>
                    {unpaid.length>0 && (
                      <div style={{fontSize:11,color:'var(--orange)',marginTop:3,fontWeight:600}}>
                        {unpaid.length} outstanding invoice{unpaid.length!==1?'s':''} · {fmtAmt(unpaid.reduce((s,i)=>s+(i.amount||0),0))}
                      </div>
                    )}
                    {!allInvoices.some(i=>i.clientId===sub.id&&i.monthYear===nowMonth()) && sub.active!==false && (
                      <div style={{fontSize:11,color:'var(--blue)',marginTop:3}}>
                        No invoice yet for {nowMonth()} — use Generate invoice above
                      </div>
                    )}
                  </div>
                  <div style={{display:'flex',gap:6,flexShrink:0,flexWrap:'wrap'}}>
                    <Btn size="sm" variant="primary" onClick={()=>{
                      setGenSub(sub);
                      setGenAmount(String(sub.amount));
                      setShowGenModal(true);
                    }}>Generate invoice</Btn>
                    <Btn size="sm" variant="ghost" onClick={()=>{setShowHistoryModal(sub);setHistoryForm({startDate:sub.startDate||'',endDate:nowMonth()});}}>Generate history</Btn>
                    <Btn size="sm" variant="ghost" onClick={()=>{setEditingSub(sub);setSubForm({name:sub.name,amount:String(sub.amount),dueDay:String(sub.dueDay),startDate:sub.startDate||'',adType:sub.adType||'',socialMedia:sub.socialMedia||false,active:sub.active!==false,notes:sub.notes||''});setShowSubModal(true);}}>
                      <Edit2 size={11}/>
                    </Btn>
                    <Btn size="sm" variant="danger" onClick={()=>deleteSub(sub.id)}><Trash2 size={11}/></Btn>
                  </div>
                </div>
              </div>
            );
          })
        }
      </Card>

      {/* One-time projects */}
      <Card>
        <SectionHeader title="One-time projects" count={(selected.projects||[]).length}
          action={<Btn size="sm" onClick={()=>{setProjectForm(emptyProject());setShowProjectModal(true);}}>
            <Plus size={12}/>Add project
          </Btn>}
        />
        {(selected.projects||[]).length===0
          ? <EmptyState message="No one-time projects yet."/>
          : (selected.projects||[]).slice().reverse().map(proj=>(
            <div key={proj.id} style={{padding:'12px 18px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:500,fontSize:13,color:'var(--text)',marginBottom:2}}>{proj.name}</div>
                <div style={{fontSize:12,color:'var(--text3)'}}>{proj.date} {proj.notes && `· ${proj.notes}`}</div>
              </div>
              <div style={{fontWeight:700,fontSize:14,color:'var(--text)',flexShrink:0,fontFamily:'var(--mono)',fontVariantNumeric:'lining-nums tabular-nums'}}>{fmtAmt(proj.amount)}</div>
              <select value={proj.status||'unpaid'} onChange={e=>updateProjectStatus(proj.id,e.target.value)}
                style={{...sel,width:'auto',padding:'4px 10px',fontSize:12,flexShrink:0}}>
                <option value="unpaid">Unpaid</option>
                <option value="invoiced">Invoiced</option>
                <option value="paid">Paid</option>
              </select>
              <Btn size="sm" variant="danger" onClick={()=>deleteProject(proj.id)}><Trash2 size={11}/></Btn>
            </div>
          ))
        }
      </Card>

      {/* Invoice history for this agency */}
      {subInvoices.length > 0 && (
        <Card>
          <SectionHeader title="Invoice history" count={subInvoices.length}/>
          <Table headers={['Invoice #','Sub-client','Month','Amount','Status']}>
            {subInvoices.slice().reverse().map(inv=>(
              <tr key={inv.id}>
                <TD mono>{inv.invoiceNumber}</TD>
                <TD muted>{inv.subClientName||selected.name}</TD>
                <TD muted>{inv.monthYear}</TD>
                <TD mono>{fmtAmt(inv.amount)}</TD>
                <TD>
                  <Badge
                    label={inv.status}
                    color={inv.status==='paid'?'green':inv.status==='pending'?'yellow':'gray'}
                  />
                </TD>
              </tr>
            ))}
          </Table>
        </Card>
      )}

      {/* Record Payment modal */}
      <Modal open={showPayModal} onClose={()=>setShowPayModal(false)} title={`Record payment — ${selected?.name}`}>
        {(selected?.creditBalance||0) > 0 && (
          <div style={{background:'var(--green-bg)',border:'1px solid rgba(143,176,138,0.25)',borderRadius:'var(--radius)',padding:'10px 12px',fontSize:12,color:'var(--green)',marginBottom:'1rem'}}>
            Existing credit balance: <strong>{fmtAmt(selected.creditBalance)}</strong> — this will be added to your new payment before applying.
          </div>
        )}
        <div style={{background:'var(--bg3)',borderRadius:'var(--radius)',padding:'10px 12px',fontSize:12,color:'var(--text3)',marginBottom:'1rem'}}>
          Payments are applied chronologically — oldest unpaid invoices are cleared first. Any leftover amount is stored as a credit balance and automatically used when the next payment is recorded.
        </div>
        <Field label="Payment amount received (PKR)">
          <Input
            type="number"
            value={payAmount}
            onChange={e=>setPayAmount(e.target.value)}
            placeholder="e.g. 500000"
            autoFocus
          />
        </Field>
        {payAmount && Number(payAmount) > 0 && selected && (() => {
          // Preview: how many invoices will this clear?
          const subIds = (selected.subClients||[]).map(s=>s.id);
          const unpaid = allInvoices
            .filter(i => (i.clientId===selected.id || subIds.includes(i.clientId)) && i.status!=='paid')
            .sort((a,b)=>(a.monthYear||'').localeCompare(b.monthYear||'')||(a.generatedDate||'').localeCompare(b.generatedDate||''));
          let running = (selected.creditBalance||0) + Number(payAmount);
          let cleared = 0;
          for (const inv of unpaid) {
            if (running >= inv.amount) { running -= inv.amount; cleared++; } else break;
          }
          return (
            <div style={{background:'var(--bg3)',borderRadius:'var(--radius)',padding:'10px 12px',fontSize:13,marginBottom:'1rem'}}>
              <div style={{color:'var(--text)',marginBottom:4}}>
                Total to apply: <strong style={{color:'var(--accent)'}}>{fmtAmt((selected.creditBalance||0)+Number(payAmount))}</strong>
                {(selected.creditBalance||0) > 0 && <span style={{fontSize:11,color:'var(--text3)'}}> (incl. {fmtAmt(selected.creditBalance)} credit)</span>}
              </div>
              <div style={{color:'var(--green)'}}>Will clear: <strong>{cleared} invoice{cleared!==1?'s':''}</strong></div>
              {running > 0 && <div style={{color:'var(--blue)',marginTop:2}}>New credit balance: <strong>{fmtAmt(running)}</strong></div>}
              {running === 0 && cleared > 0 && <div style={{color:'var(--text3)',marginTop:2}}>No credit remaining.</div>}
            </div>
          );
        })()}
        <div style={{display:'flex',justifyContent:'flex-end',gap:8}}>
          <Btn onClick={()=>setShowPayModal(false)}>Cancel</Btn>
          <Btn variant="primary" onClick={()=>{
            recordPayment(selected.id, Number(payAmount));
            setShowPayModal(false);
          }}>Apply payment</Btn>
        </div>
      </Modal>

      {/* Generate Invoice modal */}
      <Modal open={showGenModal} onClose={()=>setShowGenModal(false)} title={`Generate invoice — ${genSub?.name}`}>
        <div style={{background:'var(--bg3)',borderRadius:'var(--radius)',padding:'10px 12px',fontSize:12,color:'var(--text3)',marginBottom:'1rem'}}>
          This will generate an invoice for <strong style={{color:'var(--text)'}}>{nowMonth()}</strong> and add it to the invoice pool.
          If Parthenon has a credit balance, it will be available to apply when you next record a payment.
        </div>
        <Field label="Amount (PKR)">
          <Input
            type="number"
            value={genAmount}
            onChange={e=>setGenAmount(e.target.value)}
            autoFocus
          />
        </Field>
        {genSub && genAmount && Number(genAmount) !== genSub.amount && (
          <div style={{fontSize:12,color:'var(--orange)',marginBottom:'0.75rem'}}>
            Note: default rate for {genSub.name} is {fmtAmt(genSub.amount)}/mo. You're overriding to {fmtAmt(Number(genAmount))}.
          </div>
        )}
        {/* Check if already invoiced this month */}
        {genSub && allInvoices.some(i => i.clientId===genSub.id && i.monthYear===nowMonth()) && (
          <div style={{background:'rgba(248,113,113,0.1)',border:'1px solid rgba(248,113,113,0.2)',borderRadius:'var(--radius)',padding:'10px 12px',fontSize:12,color:'var(--red)',marginBottom:'1rem'}}>
            ⚠️ An invoice already exists for {genSub?.name} this month ({nowMonth()}).
          </div>
        )}
        <div style={{display:'flex',justifyContent:'flex-end',gap:8}}>
          <Btn onClick={()=>setShowGenModal(false)}>Cancel</Btn>
          <Btn variant="primary" onClick={()=>{
            generateSubInvoice(genSub, Number(genAmount));
            setShowGenModal(false);
          }}>Generate</Btn>
        </div>
      </Modal>

      {/* Sub-client modal */}
      <Modal open={showSubModal} onClose={()=>setShowSubModal(false)} title={editingSub?'Edit sub-client':'Add sub-client'}>
        <Field label="Client name"><Input value={subForm.name} onChange={e=>setSubForm(p=>({...p,name:e.target.value}))} placeholder="e.g. My Sewing Mall" autoFocus/></Field>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
          <Field label="Monthly amount (PKR)"><Input type="number" value={subForm.amount} onChange={e=>setSubForm(p=>({...p,amount:e.target.value}))} placeholder="e.g. 115000"/></Field>
          <Field label="Due day of month"><Input type="number" min={1} max={28} value={subForm.dueDay} onChange={e=>setSubForm(p=>({...p,dueDay:e.target.value}))} placeholder="1"/></Field>
        </div>
        <Field label="Start date (when billing began)"><Input type="date" value={subForm.startDate} onChange={e=>setSubForm(p=>({...p,startDate:e.target.value}))}/></Field>
        <Field label="Ad type (if running ads)">
          <select value={subForm.adType} onChange={e=>setSubForm(p=>({...p,adType:e.target.value}))} style={sel}>
            <option value="">No ads</option>
            <option value="E-Commerce">E-Commerce</option>
            <option value="E-Commerce & DMs">E-Commerce &amp; DMs</option>
            <option value="Service Sector">Service Sector</option>
          </select>
        </Field>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',background:'var(--bg3)',borderRadius:'var(--radius)',padding:'10px 12px',marginBottom:12}}>
          <span style={{fontSize:13,color:'var(--text)'}}>Social media management</span>
          <button onClick={()=>setSubForm(p=>({...p,socialMedia:!p.socialMedia}))} style={{width:40,height:22,borderRadius:11,border:'none',cursor:'pointer',background:subForm.socialMedia?'var(--accent)':'var(--bg4)',position:'relative',transition:'background 0.2s',flexShrink:0}}>
            <div style={{position:'absolute',top:2,left:subForm.socialMedia?20:2,width:18,height:18,borderRadius:'50%',background:subForm.socialMedia?'var(--bg)':'var(--text)',transition:'left 0.2s'}}/>
          </button>
        </div>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',background:'var(--bg3)',borderRadius:'var(--radius)',padding:'10px 12px',marginBottom:12}}>
          <span style={{fontSize:13,color:'var(--text)'}}>Currently active</span>
          <button onClick={()=>setSubForm(p=>({...p,active:!p.active}))} style={{width:40,height:22,borderRadius:11,border:'none',cursor:'pointer',background:subForm.active?'var(--accent)':'var(--bg4)',position:'relative',transition:'background 0.2s',flexShrink:0}}>
            <div style={{position:'absolute',top:2,left:subForm.active?20:2,width:18,height:18,borderRadius:'50%',background:subForm.active?'var(--bg)':'var(--text)',transition:'left 0.2s'}}/>
          </button>
        </div>
        <Field label="Notes (optional)">
          <textarea value={subForm.notes} onChange={e=>setSubForm(p=>({...p,notes:e.target.value}))} rows={2} style={{...inp,resize:'vertical',fontFamily:'var(--font)'}}/>
        </Field>
        <div style={{display:'flex',justifyContent:'flex-end',gap:8,marginTop:'1rem'}}>
          <Btn onClick={()=>setShowSubModal(false)}>Cancel</Btn>
          <Btn variant="primary" onClick={saveSub}>{editingSub?'Save changes':'Add sub-client'}</Btn>
        </div>
      </Modal>

      {/* Project modal */}
      <Modal open={showProjectModal} onClose={()=>setShowProjectModal(false)} title="Add one-time project">
        <Field label="Project name"><Input value={projectForm.name} onChange={e=>setProjectForm(p=>({...p,name:e.target.value}))} placeholder="e.g. Website redesign" autoFocus/></Field>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
          <Field label="Amount (PKR)"><Input type="number" value={projectForm.amount} onChange={e=>setProjectForm(p=>({...p,amount:e.target.value}))}/></Field>
          <Field label="Date"><Input type="date" value={projectForm.date} onChange={e=>setProjectForm(p=>({...p,date:e.target.value}))}/></Field>
        </div>
        <Field label="Notes">
          <textarea value={projectForm.notes} onChange={e=>setProjectForm(p=>({...p,notes:e.target.value}))} rows={2} style={{...inp,resize:'vertical',fontFamily:'var(--font)'}}/>
        </Field>
        <div style={{display:'flex',justifyContent:'flex-end',gap:8,marginTop:'1rem'}}>
          <Btn onClick={()=>setShowProjectModal(false)}>Cancel</Btn>
          <Btn variant="primary" onClick={saveProject}>Add project</Btn>
        </div>
      </Modal>

      {/* Historical invoice generator */}
      <Modal open={!!showHistoryModal} onClose={()=>setShowHistoryModal(null)} title={`Generate history — ${showHistoryModal?.name}`}>
        <div style={{background:'var(--blue-bg)',border:'1px solid rgba(96,165,250,0.2)',borderRadius:'var(--radius)',padding:'10px 12px',fontSize:12,color:'var(--blue)',marginBottom:'1rem'}}>
          This will generate one invoice per month between the dates you select, for PKR {fmtAmt(showHistoryModal?.amount)}/mo. Already-existing months are skipped.
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
          <Field label="From month">
            <Input type="month" value={historyForm.startDate} onChange={e=>setHistoryForm(p=>({...p,startDate:e.target.value}))}/>
          </Field>
          <Field label="To month">
            <Input type="month" value={historyForm.endDate} onChange={e=>setHistoryForm(p=>({...p,endDate:e.target.value}))}/>
          </Field>
        </div>
        {historyForm.startDate && historyForm.endDate && (
          <div style={{fontSize:13,color:'var(--text3)',marginBottom:'1rem'}}>
            {monthsBetween(historyForm.startDate,historyForm.endDate).length} month(s) · {fmtAmt((showHistoryModal?.amount||0)*monthsBetween(historyForm.startDate,historyForm.endDate).length)} total
          </div>
        )}
        <div style={{display:'flex',justifyContent:'flex-end',gap:8}}>
          <Btn onClick={()=>setShowHistoryModal(null)}>Cancel</Btn>
          <Btn variant="primary" onClick={generateHistory}>Generate invoices</Btn>
        </div>
      </Modal>
    </div>
  );
}
